<template>
  <div>
    <!-- Quick tour -->
    <quick-tour-card-wrapper
      :active="quickTourVisible"
      closable
      @close="onCloseQuickTour"
    >
      <financial-plan-quick-tour />
    </quick-tour-card-wrapper>
    <!-- /Quick tour -->

    <loader v-if="loading" />
    <template v-if="isPageContentVisible && !loading">

      <overview-summary class="mb-4" />

      <el-row class="d-flex flex-wrap mb-4" :gutter="15">
        <el-col
          class="d-flex mb-4 mb-md-0"
          :sm="24"
          :md="12"
        >
          <ib-card-empty-state
            v-if="isRevenueStreamEmpty"
            class="w-100"
            :icon="require('@/assets/img/icons/overview-icons/total-revenue-icon.svg')"
            :title="$t('pages.financialPlan.overview.linkCards.revenueStreams.title')"
            :description=" $t('pages.financialPlan.overview.linkCards.revenueStreams.description')"
            :button-text=" $t('pages.financialPlan.overview.linkCards.revenueStreams.buttonText')"
            :to="{name: 'financial-plan-revenue'}"
          />
          <!-- 339 -->
          <template v-else>
            <ib-card :animation="false" class="pt-4 pb-1 w-100">
              <revenue-over-time
                :title="$t('pages.financialPlan.navigation.revenue')"
                hide-right-side
                :height="234"
                chart-animations
              >
                <template #bottom>
                  <ib-link :to="{name: 'financial-plan-revenue'}" :text="$t('pages.financialPlan.overview.linkCards.revenueStreams.title')" />
                </template>
              </revenue-over-time>
            </ib-card>
          </template>
        </el-col>
        <el-col
          class="d-flex"
          :sm="24"
          :md="12"
        >
          <ib-card-empty-state
            v-if="isExpensesEmpty"
            class="w-100"
            :icon="require('@/assets/img/icons/overview-icons/total-costs-icon.svg')"
            :title="$t('pages.financialPlan.overview.linkCards.expenses.title')"
            :description="$t('pages.financialPlan.overview.linkCards.expenses.description')"
            :button-text="$t('pages.financialPlan.overview.linkCards.expenses.buttonText')"
            :to="{name: 'financial-plan-expenses-total-costs'}"
          />
          <template v-else>
            <ib-card :animation="false" class="pt-4 pb-2 w-100">
              <timeline-of-total-costs
                :title="$t('pages.financialPlan.navigation.expenses')"
                hide-right-side
                :height="234"
                chart-animations
              >
                <template #bottom>
                  <ib-link :to="{name: 'financial-plan-expenses-total-costs'}" :text="$t('pages.financialPlan.navigation.expenses')" />
                </template>
              </timeline-of-total-costs>
            </ib-card>
          </template>
        </el-col>
      </el-row>

      <el-row class="d-flex flex-wrap mb-4" :gutter="15">
        <el-col
          class="d-flex mb-4 mb-md-0"
          :sm="24"
          :md="12"
        >
          <ib-card-empty-chart
            v-if="isProfitAndCashFlowEmpty"
            class="w-100"
            :title="$t('pages.businessGuide.profitAndCashFlow.profitAndLoss')"
            :placeholder-image="require('@/assets/img/placeholders/placeholder-profitLoss.png')"
          />
          <ib-card v-else :animation="false" class="pb-2 pt-4 w-100">
            <profit-and-loss-projection
              hide-right-side
              :height="234"
              chart-animations
            >
              <template #bottom>
                <ib-link :to="{name: 'financial-plan-profit-and-cash-flow'}" :text="$t('pages.businessGuide.profitAndCashFlow.profitAndLoss')" />
              </template>
            </profit-and-loss-projection>
          </ib-card>
        </el-col>
        <el-col
          class="d-flex"
          :sm="24"
          :md="12"
        >
          <ib-card-empty-chart
            v-if="isProfitAndCashFlowEmpty"
            class="w-100"
            :title="$t('pages.businessGuide.profitAndCashFlow.cashFlow')"
            :placeholder-image="require('@/assets/img/placeholders/placeholder-cashFlow.png')"
          />
          <ib-card v-else :animation="false" class="pb-2 pt-4 w-100">
            <cash-flow-projection
              hide-right-side
              :height="234"
              chart-animations
            >
              <template #bottom>
                <ib-link :to="{name: 'financial-plan-profit-and-cash-flow'}" :text="$t('pages.businessGuide.profitAndCashFlow.cashFlow')" />
              </template>
            </cash-flow-projection>
          </ib-card>
        </el-col>
      </el-row>
    </template>
  </div>
</template>

<script>

import CashFlowProjection from '@/views/Home/StoryMode/FinancialPlan/Components/Overview/CashFlowProjection'
import EventBus from '@/event-bus'
import FinancialPlanQuickTour from '@/views/Home/Components/QuickTours/FinancialPlanQuickTour'
import IbCardEmptyChart from '@/components/_v2/IbCardEmptyChart.vue'
import IbCardEmptyState from '@/components/_v2/IbCardEmptyState.vue'
import Loader from '@/components/Loader'
import MixinApiCalculations from '@/mixins/apiCalculations'
import MixinGuide from '@/mixins/guide'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import MixinTable from '@/mixins/table'
import OverviewSummary from '@/views/Home/StoryMode/FinancialPlan/Summary/Summary'
import ProfitAndLossProjection from '@/views/Home/StoryMode/FinancialPlan/Components/Overview/ProfitAndLossProjection'
import QuickTourCardWrapper from '@/views/Home/Components/QuickTours/QuickTourCardWrapper'
import addNewDropdownOptions from '@/data/financialPlanOverviewAddNewDropdownOptions'

import { mapGetters, mapState } from 'vuex'
import RevenueOverTime from '@/views/Home/StoryMode/FinancialPlan/Components/Revenue/RevenueOverTime.vue'
import TimelineOfTotalCosts
  from '@/views/Home/StoryMode/FinancialPlan/Components/Expenses/TotalCost/TimelineOfTotalCosts.vue'

export default {
  name: 'Overview',

  components: {
    IbCardEmptyChart,
    IbCardEmptyState,
    TimelineOfTotalCosts,
    RevenueOverTime,
    CashFlowProjection,
    FinancialPlanQuickTour,
    Loader,
    OverviewSummary,
    ProfitAndLossProjection,
    QuickTourCardWrapper
  },

  mixins: [
    MixinApiCalculations,
    MixinGuide,
    MixinIdeaRoles,
    MixinTable
  ],

  data () {
    return {
      loading: false,
      quickTourVisible: false,
      isPageContentVisible: true,
      addNewDropdownOptions
    }
  },

  computed: {
    ...mapState(['idea']),
    ...mapGetters('idea', ['isOverviewEverUpdated']),

    isExpensesEmpty () {
      return !(
        this.idea.storyMode.setUp.startupCosts.startupCosts.length !== 0 ||
        this.idea.storyMode.setUp.staffAndPayrolls.staffAndPayrolls.length !== 0 ||
        this.idea.storyMode.forecast.directCosts.directCosts.length !== 0 ||
        this.idea.storyMode.forecast.marketingBudget.marketingElements.length !== 0 ||
        this.idea.storyMode.forecast.otherOverheads.otherOverheads.length !== 0 ||
        this.idea.storyMode.setUp.financing.finances.length !== 0
      )
    },

    isRevenueStreamEmpty () {
      return !this.idea.storyMode.forecast.revenueStreams.revenueStreams.length
    },

    isProfitAndCashFlowEmpty () {
      return !(
        this.idea.storyMode.forecast.revenueStreams.revenueStreams.length !== 0 ||
          this.idea.storyMode.forecast.directCosts.directCosts.length !== 0 ||
          this.idea.storyMode.setUp.staffAndPayrolls.staffAndPayrolls.length !== 0 ||
          this.idea.storyMode.forecast.marketingBudget.marketingElements.length !== 0 ||
          this.idea.storyMode.forecast.otherOverheads.otherOverheads.length !== 0 ||
          this.idea.storyMode.setUp.startupCosts.startupCosts.length !== 0 ||
          this.idea.storyMode.setUp.financing.finances.length !== 0
      )
    }
  },

  watch: {
    'quickTourVisible' (active) {
      if (active) {
        EventBus.$emit('open-quick-tour')
      }
    }
  },

  created () {
    this.openQuickTour('financialPlanQuickTour')
  },

  mounted () {
    EventBus.$on('toggle-quick-tour', (payLoad) => {
      this.quickTourVisible = payLoad.active
      this.isPageContentVisible = true
    })
  },

  methods: {
    onCloseQuickTour () {
      this.quickTourVisible = false
      this.isPageContentVisible = true
      EventBus.$emit('close-quick-tour')
    }
  }
}
</script>
